import axios, { AxiosResponse } from 'axios'
import qs from 'qs'
import { groupBy } from 'lodash'
import { convertFromImageToPdf, mergePdfs } from 'services/Pdf.services'
import { MediaAsset } from 'pages/WorkspacePage/ChannelView/ChannelInterfaceProvider/ChannelInterface.types'
import { NO_CUSTOM_HEADERS_SENTINEL } from 'features/auth/Auth.constants'

export async function downloadFiles(urls: string[]): Promise<Blob[]> {
  return Promise.all(urls.map(downloadFile))
}

export async function downloadFile(url: string): Promise<Blob> {
  const result: AxiosResponse<BlobPart> = await axios.request({
    url: modifyUrl(url),
    method: 'GET',
    responseType: 'arraybuffer',
    headers: {
      noCustomHeaders: NO_CUSTOM_HEADERS_SENTINEL,
    },
  })
  return new Blob([result.data], { type: 'application/octet-stream' })
}

/**
 * Modifies the URL to avoid cache issues.
 */
export function modifyUrl(url: string): string {
  const urlObj = new URL(url)
  const queryParams = urlObj.search ? qs.parse(urlObj.search, { ignoreQueryPrefix: true }) : {}
  queryParams.notFromCache = 'true'
  return (
    urlObj.origin +
    (urlObj.pathname === '/' ? '' : urlObj.pathname) +
    qs.stringify(queryParams, { addQueryPrefix: true })
  )
}

export function downloadAssetToMachine(asset: Blob, filename: string) {
  const fileUrl = URL.createObjectURL(asset)
  const downloadLink = document.createElement('a')
  downloadLink.href = fileUrl
  downloadLink.download = filename
  downloadLink.click()
  downloadLink.remove()
}

type IsImageOrPdfResult = { valid: boolean; type: 'image' | 'pdf' | null }
export function isImageOrPdf(asset: MediaAsset): IsImageOrPdfResult {
  const result: IsImageOrPdfResult = { valid: false, type: null }
  const mimeParts = asset.mimeType?.split('/')
  if (mimeParts?.length && mimeParts[0] === 'image') {
    result.type = 'image'
    result.valid = true
  } else if (asset.mimeType === 'application/pdf') {
    result.type = 'pdf'
    result.valid = true
  }

  return result
}

export function isImage(asset: MediaAsset) {
  let result = false
  const mimeParts = asset.mimeType?.split('/')
  if (mimeParts?.length && mimeParts[0] === 'image') {
    result = true
  }

  return result
}

interface PdfConversionMediaAsset extends MediaAsset {
  originalIndex: number
  blob: Blob | null
}
/**
 * This returns all assets as a PDF, however, it ignores anything that is not an image or PDF.
 */
export async function convertAssetsToPdf(assets: MediaAsset[]) {
  if (!assets.length) {
    throw new Error('No assets to convert to PDF')
  }
  // we want to first group images and pdfs, while disregarding any other file types
  const grouped = getAssetsByPDFCompatibleCategory(assets)
  const images = grouped.images ? await Promise.all(grouped.images.map(fetchAndAppendPdfAssetBlob)) : []
  const pdfs = grouped.pdfs ? await Promise.all(grouped.pdfs.map(fetchAndAppendPdfAssetBlob)) : []
  const collection = pdfs
    .concat(images)
    .sort((a, b) => a.originalIndex - b.originalIndex)
    .filter(({ blob }) => !!blob)
  let file: Blob | null = null
  // merge all pdfs if necessary, so we only have one pdf document
  if (collection.length > 1) {
    const merged = await mergePdfs(collection.map(({ blob }) => blob!))
    file = new Blob([merged], { type: 'application/pdf' })!
  } else {
    file = collection[0].blob!
  }

  return file
}

async function fetchAndAppendPdfAssetBlob(asset: PdfConversionMediaAsset) {
  if (asset.url) {
    asset.blob = await downloadFile(asset.url)

    if (asset.mimeType?.includes('image') && asset.blob) {
      asset.blob = await convertFromImageToPdf(asset.blob as File)
    }
  }

  return asset
}

export function getAssetsByPDFCompatibleCategory(assets: MediaAsset[]) {
  const conversionAssets: PdfConversionMediaAsset[] = assets.map((asset, index) => ({
    ...asset,
    originalIndex: index,
    blob: null,
  }))
  return groupBy(conversionAssets, (asset) => {
    const result = isImageOrPdf(asset)
    if (result.type === 'image') {
      return 'images'
    }
    if (result.type === 'pdf') {
      return 'pdfs'
    }
    return 'other'
  })
}

export function downloadAsCSV(csvData: string, filename: string) {
  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
